import { useState, useEffect, useMemo } from "react";
import { Mail, Inbox, Send, FileText, Star, Trash2, RefreshCw, Edit, LogIn, Sparkles, Search, Archive, Flag, ChevronLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";

const folders = [
  { id: "inbox", label: "Inbox", icon: Inbox },
  { id: "flagged", label: "Flagged", icon: Flag },
  { id: "drafts", label: "Drafts", icon: FileText },
  { id: "sent", label: "Sent", icon: Send },
  { id: "trash", label: "Trash", icon: Trash2 },
  { id: "archive", label: "Archive", icon: Archive },
];

interface GmailMessage {
  id: string;
  threadId: string;
  snippet: string;
  payload?: {
    headers: Array<{ name: string; value: string }>;
    body?: { data?: string };
    parts?: Array<{ body?: { data?: string }; mimeType?: string }>;
  };
  labelIds?: string[];
  internalDate?: string;
}

function EmailWorkspaceContent() {
  const navigate = useNavigate();
  const [selectedFolder, setSelectedFolder] = useState("inbox");
  const [emails, setEmails] = useState<any[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<any>(null);
  const [isComposing, setIsComposing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isGeneratingReply, setIsGeneratingReply] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [emailStats, setEmailStats] = useState({
    total: 0,
    unread: 0,
    flagged: 0,
    sent: 0
  });
  const { toast } = useToast();

  const [composeForm, setComposeForm] = useState({
    to: "",
    subject: "",
    body: "",
  });
  const [aiDescription, setAiDescription] = useState("");
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);

  // Update stats whenever emails change
  useEffect(() => {
    if (emails.length > 0) {
      setEmailStats({
        total: emails.length,
        unread: emails.filter(e => !e.is_read).length,
        flagged: emails.filter(e => e.is_starred).length,
        sent: selectedFolder === 'sent' ? emails.length : emailStats.sent
      });
    } else if (selectedFolder === 'inbox') {
      // Reset stats when no emails in inbox
      setEmailStats({ total: 0, unread: 0, flagged: 0, sent: emailStats.sent });
    }
  }, [emails, selectedFolder]);

  // Memoize filtered emails for performance
  const filteredEmails = useMemo(() => {
    if (!searchQuery.trim()) return emails;
    const query = searchQuery.toLowerCase();
    return emails.filter(email => 
      email.subject?.toLowerCase().includes(query) ||
      email.from_name?.toLowerCase().includes(query) ||
      email.from_email?.toLowerCase().includes(query) ||
      email.body_text?.toLowerCase().includes(query)
    );
  }, [emails, searchQuery]);

  // Memoize folder counts for performance
  const folderCounts = useMemo(() => ({
    inbox: emails.filter(e => !e.is_read).length,
    flagged: emails.filter(e => e.is_starred).length,
    sent: selectedFolder === 'sent' ? emails.length : 0,
    drafts: 0,
    trash: 0,
    archive: 0
  }), [emails, selectedFolder]);

  // Check for pre-filled recipient from session storage
  useEffect(() => {
    const prefilledRecipient = sessionStorage.getItem('email_compose_to');
    if (prefilledRecipient) {
      setComposeForm(prev => ({ ...prev, to: prefilledRecipient }));
      setIsComposing(true);
      sessionStorage.removeItem('email_compose_to');
      toast({
        title: "Compose email",
        description: `Starting new email to ${prefilledRecipient}`,
      });
    }
  }, []);

  // Check connection status on mount
  useEffect(() => {
    checkGmailConnection();
  }, []);

  const checkGmailConnection = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('gmail-oauth', {
        body: { action: 'status' }
      });

      if (error) throw error;

      if (data?.connected && data?.access_token) {
        setAccessToken(data.access_token);
        setIsConnected(true);
        fetchGmailMessages(data.access_token);
      }
    } catch (error: any) {
      console.error('Connection check failed:', error);
    }
  };

  const startGmailAuth = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('gmail-oauth', {
        body: { action: 'authorize' }
      });

      if (error) throw error;

      if (data?.auth_url) {
        // Open OAuth window
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        
        window.open(
          data.auth_url,
          'Gmail Authorization',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        // Poll for connection
        const checkInterval = setInterval(async () => {
          const { data: statusData } = await supabase.functions.invoke('gmail-oauth', {
            body: { action: 'status' }
          });

          if (statusData?.connected) {
            clearInterval(checkInterval);
            setAccessToken(statusData.access_token);
            setIsConnected(true);
            setLoading(false);
            toast({
              title: "Connected to Gmail",
              description: "Loading your emails...",
            });
            fetchGmailMessages(statusData.access_token);
          }
        }, 2000);

        // Stop polling after 2 minutes
        setTimeout(() => clearInterval(checkInterval), 120000);
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      toast({
        title: "Authentication failed",
        description: error.message || "Could not connect to Google",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const disconnectGmail = async () => {
    try {
      const { error } = await supabase.functions.invoke('gmail-oauth', {
        body: { action: 'disconnect' }
      });

      if (error) throw error;

      setAccessToken(null);
      setIsConnected(false);
      setEmails([]);
      toast({
        title: "Disconnected from Gmail",
        description: "You can reconnect anytime",
      });
    } catch (error: any) {
      console.error('Disconnect error:', error);
      toast({
        title: "Error",
        description: "Failed to disconnect",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (accessToken && isConnected) {
      fetchGmailMessages(accessToken);
    }
  }, [selectedFolder, isConnected]);

  const fetchGmailMessages = async (token: string) => {
    setLoading(true);
    try {
      let labelQuery = '';
      switch (selectedFolder) {
        case 'inbox':
          labelQuery = 'INBOX';
          break;
        case 'sent':
          labelQuery = 'SENT';
          break;
        case 'drafts':
          labelQuery = 'DRAFT';
          break;
        case 'starred':
          labelQuery = 'STARRED';
          break;
        case 'trash':
          labelQuery = 'TRASH';
          break;
        default:
          labelQuery = 'INBOX';
      }

      let allMessageIds: { id: string }[] = [];
      let pageToken: string | undefined = undefined;

      // Fetch all message IDs using pagination
      do {
        const url = pageToken 
          ? `https://gmail.googleapis.com/gmail/v1/users/me/messages?labelIds=${labelQuery}&maxResults=100&pageToken=${pageToken}`
          : `https://gmail.googleapis.com/gmail/v1/users/me/messages?labelIds=${labelQuery}&maxResults=100`;
        
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          // Handle expired/invalid token
          if (response.status === 401 || response.status === 403) {
            localStorage.removeItem('gmail_access_token');
            setAccessToken(null);
            throw new Error('Session expired. Please sign in again.');
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error?.message || 'Failed to fetch messages');
        }

        const data = await response.json();
        
        if (!data.messages || data.messages.length === 0) {
          if (allMessageIds.length === 0) {
            setEmails([]);
            setLoading(false);
            return;
          }
          break;
        }

        allMessageIds = [...allMessageIds, ...data.messages];
        pageToken = data.nextPageToken;
        
        // Limit to first 100 for performance
        if (allMessageIds.length >= 100) break;
        
      } while (pageToken);

      console.log(`Fetched ${allMessageIds.length} emails from ${selectedFolder}`);

      // Fetch full details for messages in batches
      const batchSize = 20;
      const fullMessages: any[] = [];
      
      for (let i = 0; i < Math.min(allMessageIds.length, 100); i += batchSize) {
        const batch = allMessageIds.slice(i, i + batchSize);
        const batchMessages = await Promise.all(
          batch.map(async (msg: { id: string }) => {
            const msgResponse = await fetch(
              `https://gmail.googleapis.com/gmail/v1/users/me/messages/${msg.id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            return msgResponse.json();
          })
        );
        fullMessages.push(...batchMessages);
      }

      const formattedEmails = fullMessages.map((msg: GmailMessage) => {
        const headers = msg.payload?.headers || [];
        const getHeader = (name: string) => 
          headers.find((h) => h.name.toLowerCase() === name.toLowerCase())?.value || '';

        const subject = getHeader('Subject');
        const from = getHeader('From');
        const to = getHeader('To');
        const date = msg.internalDate ? new Date(parseInt(msg.internalDate)) : new Date();
        
        // Extract body
        let body = msg.snippet || '';
        if (msg.payload?.body?.data) {
          body = atob(msg.payload.body.data.replace(/-/g, '+').replace(/_/g, '/'));
        } else if (msg.payload?.parts) {
          const textPart = msg.payload.parts.find(p => p.mimeType === 'text/plain');
          if (textPart?.body?.data) {
            body = atob(textPart.body.data.replace(/-/g, '+').replace(/_/g, '/'));
          }
        }

        const isUnread = msg.labelIds?.includes('UNREAD') ?? false;
        const isStarred = msg.labelIds?.includes('STARRED') ?? false;

        return {
          id: msg.id,
          subject: subject || '(No Subject)',
          from_email: from,
          from_name: from.split('<')[0].trim(),
          to_email: [to],
          body_text: body,
          created_at: date.toISOString(),
          is_read: !isUnread,
          is_starred: isStarred,
          folder: selectedFolder,
        };
      });

      setEmails(formattedEmails);
    } catch (error: any) {
      console.error('Gmail fetch error:', error);
      
      // Check if it's an auth error
      const isAuthError = error.message?.includes('Session expired') || 
                         error.message?.includes('401') || 
                         error.message?.includes('403') ||
                         error.message?.includes('invalid_grant');
      
      if (isAuthError) {
        setAccessToken(null);
        setIsConnected(false);
        toast({
          title: "Session Expired",
          description: "Reconnecting...",
        });
        // Auto-reconnect by checking connection (will refresh token)
        checkGmailConnection();
      } else {
        toast({
          title: "Error loading emails",
          description: error.message || "Failed to connect to Gmail",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCompose = async () => {
    if (!accessToken) {
      toast({
        title: "Not authenticated",
        description: "Please sign in with Google first",
        variant: "destructive",
      });
      return;
    }

    try {
      const email = [
        `To: ${composeForm.to}`,
        `Subject: ${composeForm.subject}`,
        '',
        composeForm.body
      ].join('\n');

      const encodedEmail = btoa(email)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

      const response = await fetch(
        'https://gmail.googleapis.com/gmail/v1/users/me/messages/send',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            raw: encodedEmail,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      toast({
        title: "Email sent",
        description: "Your email has been sent successfully",
      });

      setIsComposing(false);
      setComposeForm({ to: "", subject: "", body: "" });
      
      if (selectedFolder === 'sent') {
        fetchGmailMessages(accessToken);
      }
    } catch (error: any) {
      toast({
        title: "Error sending email",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const markAsRead = async (messageId: string) => {
    if (!accessToken) return;

    try {
      await fetch(
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}/modify`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            removeLabelIds: ['UNREAD'],
          }),
        }
      );
      
      setEmails(prev => prev.map(e => 
        e.id === messageId ? { ...e, is_read: true } : e
      ));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const toggleStar = async (messageId: string, currentStarred: boolean) => {
    if (!accessToken) return;

    try {
      await fetch(
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}/modify`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            addLabelIds: currentStarred ? [] : ['STARRED'],
            removeLabelIds: currentStarred ? ['STARRED'] : [],
          }),
        }
      );
      
      setEmails(prev => prev.map(e => 
        e.id === messageId ? { ...e, is_starred: !currentStarred } : e
      ));
    } catch (error) {
      console.error('Failed to toggle star:', error);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem('gmail_access_token');
    setAccessToken(null);
    setEmails([]);
    setSelectedEmail(null);
    toast({
      title: "Signed out",
      description: "You have been signed out of Gmail",
    });
  };

  const generateAIContent = async () => {
    if (!aiDescription.trim()) {
      toast({
        title: "Description required",
        description: "Please describe what you want the email to say",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingContent(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-email-content', {
        body: {
          description: aiDescription,
          recipient: composeForm.to || null
        }
      });

      if (error) throw error;

      if (data?.content) {
        setComposeForm(prev => ({ ...prev, body: data.content }));
        setAiDescription("");
        toast({
          title: "Email generated",
          description: "AI has written your email content",
        });
      }
    } catch (error: any) {
      console.error('Error generating email:', error);
      toast({
        title: "Failed to generate email",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingContent(false);
    }
  };

  const generateAIReply = async (email: any) => {
    setIsGeneratingReply(true);
    try {
      const { data, error } = await supabase.functions.invoke('email-ai-reply', {
        body: {
          originalEmail: {
            from: email.from_email,
            subject: email.subject,
            body: email.body_text
          }
        }
      });

      if (error) throw error;

      if (data?.reply) {
        setComposeForm({
          to: email.from_email,
          subject: email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
          body: data.reply
        });
        setIsComposing(true);
        toast({
          title: "AI Reply Generated",
          description: "Review and edit the draft before sending",
        });
      }
    } catch (error: any) {
      console.error('Error generating AI reply:', error);
      toast({
        title: "Failed to generate reply",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingReply(false);
    }
  };

  if (!accessToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-background">
        {/* Header */}
        <div className="border-b border-border/50 bg-background/80 backdrop-blur-sm">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="rounded-full"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <Button onClick={startGmailAuth} className="bg-success hover:bg-success/90" disabled={loading}>
                  <Mail className="w-4 h-4 mr-2" />
                  {loading ? 'Connecting...' : 'Connect Gmail Account'}
                </Button>
                <Button variant="outline" onClick={() => setIsComposing(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Compose
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 py-6">
          <div className="grid grid-cols-12 gap-6">
            {/* Sidebar */}
            <div className="col-span-3">
              <Card className="p-4 border-border/50 bg-background/50 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-success" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-success">InboxGenius</h1>
                    <p className="text-xs text-muted-foreground">Centralized email management hub</p>
                  </div>
                </div>
                
                <div className="space-y-1 mb-6">
                  {folders.map((folder) => (
                    <button
                      key={folder.id}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all hover:bg-muted/50 group"
                    >
                      <div className="flex items-center gap-3">
                        <folder.icon className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                        <span className="text-sm font-medium">{folder.label}</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">0</Badge>
                    </button>
                  ))}
                </div>

                <div className="border-t border-border/50 pt-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Connection</p>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-destructive/10">
                    <div className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
                    <span className="text-sm font-medium text-destructive">Disconnected</span>
                  </div>
                  <Button onClick={startGmailAuth} className="w-full mt-3 bg-success hover:bg-success/90" disabled={loading}>
                    <Mail className="w-4 h-4 mr-2" />
                    Connect Gmail
                  </Button>
                </div>
              </Card>
            </div>

            {/* Main Content - Empty State */}
            <div className="col-span-9">
              <Card className="p-12 border-border/50 bg-background/50 backdrop-blur-sm flex items-center justify-center min-h-[600px]">
                <div className="text-center space-y-6 max-w-md animate-fade-in">
                  <div className="w-24 h-24 mx-auto rounded-2xl bg-success/20 flex items-center justify-center animate-scale-in">
                    <Mail className="w-12 h-12 text-success" />
                  </div>
                  <div className="space-y-3">
                    <h2 className="text-3xl font-bold">Connect Your Gmail</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Connect your Gmail account to manage emails, send AI-powered responses, and handle customer communications all in one place.
                    </p>
                  </div>
                  <Button onClick={startGmailAuth} size="lg" className="bg-success hover:bg-success/90" disabled={loading}>
                    <Mail className="w-4 h-4 mr-2" />
                    {loading ? 'Connecting...' : 'Connect Gmail Account'}
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-background">
      {/* Header */}
      <div className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="rounded-full"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-success" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-success">InboxGenius</h1>
                  <p className="text-xs text-muted-foreground">Centralized email management hub</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => accessToken && fetchGmailMessages(accessToken)}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Dialog open={isComposing} onOpenChange={setIsComposing}>
              <DialogTrigger asChild>
                <Button>
                  <Edit className="w-4 h-4 mr-2" />
                  Compose
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>New Email</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Input
                    placeholder="To"
                    value={composeForm.to}
                    onChange={(e) => setComposeForm({ ...composeForm, to: e.target.value })}
                  />
                  <Input
                    placeholder="Subject"
                    value={composeForm.subject}
                    onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                  />
                  
                  {/* AI Email Writer */}
                  <div className="border rounded-lg p-4 bg-muted/50">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">AI Email Writer</span>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Describe what the email should be about..."
                        value={aiDescription}
                        onChange={(e) => setAiDescription(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && generateAIContent()}
                        disabled={isGeneratingContent}
                      />
                      <Button 
                        onClick={generateAIContent}
                        disabled={isGeneratingContent || !aiDescription.trim()}
                        size="sm"
                      >
                        {isGeneratingContent ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Generate
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <Textarea
                    placeholder="Message"
                    rows={10}
                    value={composeForm.body}
                    onChange={(e) => setComposeForm({ ...composeForm, body: e.target.value })}
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsComposing(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCompose}>Send</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
              <Button variant="outline" size="sm" onClick={disconnectGmail}>
                Disconnect
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="container mx-auto px-6 py-4">
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="p-4 border-border/50 bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20 hover:scale-105 transition-transform animate-fade-in">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
                <p className="text-2xl font-bold">{emailStats.total}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-border/50 bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20 hover:scale-105 transition-transform animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Inbox className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Unread</p>
                <p className="text-2xl font-bold">{emailStats.unread}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-border/50 bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20 hover:scale-105 transition-transform animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Flagged</p>
                <p className="text-2xl font-bold">{emailStats.flagged}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-border/50 bg-gradient-to-br from-success/10 to-success/5 border-success/20 hover:scale-105 transition-transform animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
                <Send className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Sent</p>
                <p className="text-2xl font-bold">{emailStats.sent}</p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className="container mx-auto px-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-3">
            <Card className="p-4 border-border/50 bg-background/50 backdrop-blur-sm">
              <div className="space-y-1 mb-6">
                {folders.map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => setSelectedFolder(folder.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all hover:bg-muted/50 group ${
                      selectedFolder === folder.id ? "bg-success/10 border border-success/20" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <folder.icon className={`w-4 h-4 transition-colors ${
                        selectedFolder === folder.id ? "text-success" : "text-muted-foreground group-hover:text-foreground"
                      }`} />
                      <span className="text-sm font-medium">{folder.label}</span>
                    </div>
                    <Badge variant={selectedFolder === folder.id ? "default" : "secondary"} className="text-xs bg-muted/50">
                      {folderCounts[folder.id as keyof typeof folderCounts] || 0}
                    </Badge>
                  </button>
                ))}
              </div>

              <div className="border-t border-border/50 pt-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Connection</p>
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-success/10">
                  <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                  <span className="text-sm font-medium text-success">Connected</span>
                </div>
                <Button onClick={disconnectGmail} variant="outline" className="w-full mt-3" size="sm">
                  Disconnect Gmail
                </Button>
              </div>
            </Card>
          </div>

          {/* Email List */}
          <div className="col-span-4">
            <Card className="border-border/50 bg-background/50 backdrop-blur-sm">
              <div className="p-4 border-b border-border/50">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search emails..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-background/50"
                  />
                </div>
              </div>
              <ScrollArea className="h-[calc(100vh-16rem)]">
                {loading ? (
                  <div className="p-4 text-center text-muted-foreground">Loading...</div>
                ) : filteredEmails.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    {searchQuery.trim() ? "No emails match your search" : "No emails"}
                  </div>
                ) : (
                  <div className="divide-y divide-border/50">
                    {filteredEmails.map((email) => (
                      <button
                        key={email.id}
                        onClick={() => {
                          setSelectedEmail(email);
                          markAsRead(email.id);
                        }}
                        className="w-full p-4 text-left hover:bg-muted/50 transition-colors border-l border-transparent hover:border-l-success/40"
                      >
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {!email.is_read && (
                              <div className="w-2 h-2 rounded-full bg-success flex-shrink-0" />
                            )}
                            <span className={`truncate ${!email.is_read ? "font-semibold" : "font-medium"}`}>
                              {email.from_name || email.from_email}
                            </span>
                            {email.is_starred && <Star className="w-4 h-4 fill-amber-400 text-amber-400 flex-shrink-0" />}
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                            {email.created_at && formatDistanceToNow(new Date(email.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        <div className={`truncate mb-1 ${!email.is_read ? "font-semibold" : "font-medium"}`}>{email.subject}</div>
                        <div className="text-sm text-muted-foreground truncate">{email.body_text}</div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </Card>
          </div>

          {/* Email Content */}
          <div className="col-span-5">
            <Card className="p-6 h-[calc(100vh-22rem)] border-border/50 bg-background/50 backdrop-blur-sm">
              {selectedEmail ? (
                <div className="flex flex-col h-full">
                  <div className="flex items-start justify-between mb-4 pb-4 border-b">
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold mb-2">{selectedEmail.subject}</h2>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="font-medium">{selectedEmail.from_name || selectedEmail.from_email}</span>
                        <span>to</span>
                        <span>{selectedEmail.to_email?.join(", ")}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => generateAIReply(selectedEmail)}
                        disabled={isGeneratingReply}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        {isGeneratingReply ? "Generating..." : "AI Reply"}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleStar(selectedEmail.id, selectedEmail.is_starred)}
                      >
                        <Star className={`w-4 h-4 ${selectedEmail.is_starred ? "fill-yellow-400 text-yellow-400" : ""}`} />
                      </Button>
                    </div>
                  </div>
                  <ScrollArea className="flex-1">
                    <div className="prose prose-sm max-w-none">
                      <p className="whitespace-pre-wrap">{selectedEmail.body_text}</p>
                    </div>
                  </ScrollArea>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  Select an email to read
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EmailWorkspace() {
  return <EmailWorkspaceContent />;
}
